/**
 * TPL Cart Event Bridge
 * Intercepts product form submissions across the site
 * Submits to /cart/add.js via fetch, then dispatches 'cart:item-added'
 * Handles Protective Glass Glazing add-on: adds glazing variant BEFORE dispatching events
 * so the cart drawer always opens with the complete order.
 */
(function() {
  'use strict';

  /* TPL patch 2026-04-17 (grill v2 #11): sanitize user-visible strings before
     injecting into cart line-item properties. Prevents JSON breakage from
     quotes/backslashes/newlines and XSS in any future display context. */
  function safeStr(v) {
    if (typeof v !== 'string') return String(v || '');
    return v.replace(/[<>"\\]/g, function(c) {
      return { '<': '&lt;', '>': '&gt;', '"': '&quot;', '\\': '\\\\' }[c];
    });
  }

  /* Spinner + error helpers (D-2, D-3). Spinner already in DOM via the
     loading-spinner snippet inside the submit button; we toggle the
     `hidden` class. Error wrapper is a sibling sibling-of-form <div role="alert">
     rendered by buy-buttons.liquid; populating it announces to screen readers. */
  function showSpinner(submitButton) {
    if (!submitButton) return;
    var s = submitButton.querySelector('.loading__spinner');
    if (s) s.classList.remove('hidden');
  }
  function hideSpinner(submitButton) {
    if (!submitButton) return;
    var s = submitButton.querySelector('.loading__spinner');
    if (s) s.classList.add('hidden');
  }
  function showFormError(form, message) {
    if (!form || !message) return;
    var wrap = form.parentElement && form.parentElement.querySelector('.product-form__error-message-wrapper');
    if (!wrap) return;
    var msgEl = wrap.querySelector('.product-form__error-message');
    if (msgEl) msgEl.textContent = String(message);
    wrap.removeAttribute('hidden');
  }
  function hideFormError(form) {
    if (!form) return;
    var wrap = form.parentElement && form.parentElement.querySelector('.product-form__error-message-wrapper');
    if (wrap) wrap.setAttribute('hidden', '');
  }

  /* D-5: read glazing variant ID from the DOM at submit time so we don't
     depend on event-ordering. The container's data-current-variant-id is
     updated by tpl-glazing.js whenever Size changes. Eliminates the race
     window where a deep-link page-load could fire ATC before the first
     CustomEvent dispatched. */
  function getCurrentGlazingVariantId() {
    var container = document.getElementById('tpl-uv-glazing-container');
    if (!container) return 0;
    var raw = container.dataset.currentVariantId || container.getAttribute('data-current-variant-id');
    var n = parseInt(raw, 10);
    return isNaN(n) ? 0 : n;
  }

  let isSubmitting = false;

  // Capture phase: intercept BEFORE Dawn's product-form.js (which listens on the
  // form element) to prevent double-add and renderContents crash on TPL's cart-drawer.
  document.addEventListener('submit', async function(e) {
    const form = e.target;

    // Only intercept product forms (Shopify's add-to-cart forms)
    if (!form.matches('form[action="/cart/add"]') && !form.matches('product-form form')) return;

    // Stop the event from reaching product-form.js on the <form> element
    e.stopPropagation();

    if (isSubmitting) { e.preventDefault(); return; }
    isSubmitting = true;

    e.preventDefault();

    const submitButton = form.querySelector('[type="submit"]');
    const originalText = submitButton ? submitButton.textContent : '';
    const defaultAddText = submitButton
      ? (submitButton.getAttribute('data-add-text') || originalText)
      : '';

    // Clear any prior error before the new attempt.
    hideFormError(form);

    // Update button state
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('is-loading');
      const btnText = submitButton.querySelector('.btn-text');
      if (btnText) {
        btnText.textContent = 'Adding...';
      } else {
        submitButton.textContent = 'Adding...';
      }
      showSpinner(submitButton);
    }

    try {
      const formData = new FormData(form);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: formData
      });

      // Content-type guard: if Shopify returns HTML (auth redirect, password page,
      // preview-token edge case), JSON.parse would throw "Unexpected token '<'".
      // Fail with a clear message instead of surfacing a SyntaxError in the button.
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const looksLikeJson = contentType.includes('json') || contentType.includes('javascript');

      if (!response.ok) {
        let errDesc = 'Could not add to cart';
        if (looksLikeJson) {
          try { errDesc = (await response.json()).description || errDesc; } catch (_) {}
        } else if (response.status === 429 || response.status === 403) {
          // Bot-protection challenge intercepted the XHR — a challenge can't be
          // solved inside fetch, but a full page refresh runs it and clears the block.
          errDesc = 'Could not add to cart. Please refresh the page and try again.';
        }
        throw new Error(errDesc);
      }

      if (!looksLikeJson) {
        throw new Error('Cart unavailable. Please refresh.');
      }

      const item = await response.json();

      // ── Protective Glass Glazing Add-On ──
      // If the glazing checkbox is checked and a variant ID is available,
      // add the glazing product BEFORE dispatching cart events.
      // This eliminates the race condition where the drawer opened
      // before glazing was in the cart.
      const glazingCheckbox = document.getElementById('tpl-uv-glazing-checkbox');
      const glazingVariantId = getCurrentGlazingVariantId();

      // Validate variant ID against the server-rendered allowlist
      var glazingDataEl = document.getElementById('tpl-glazing-price-data');
      var allowedGlazingIds = new Set();
      if (glazingDataEl) {
        var dataSpans = glazingDataEl.querySelectorAll('[data-glazing-variant-id]');
        dataSpans.forEach(function(span) {
          allowedGlazingIds.add(parseInt(span.dataset.glazingVariantId, 10));
        });
      }

      // getCurrentGlazingVariantId() already returns a parsed int (0 on failure)
      if (glazingCheckbox && glazingCheckbox.checked && glazingVariantId > 0 && allowedGlazingIds.has(glazingVariantId)) {
        try {
          const glazingResp = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [{
                id: glazingVariantId,
                quantity: 1,
                properties: {
                  'For': safeStr(item.product_title)
                }
              }]
            })
          });

          if (!glazingResp.ok) {
            console.error('UV Glazing add failed');
            // Uncheck the box so the UI reflects the failed add-on
            glazingCheckbox.checked = false;
            const glazingDesc = document.getElementById('tpl-uv-glazing-desc');
            if (glazingDesc) glazingDesc.classList.remove('is-visible');
            const subtotal = document.getElementById('tpl-glazing-subtotal');
            if (subtotal) subtotal.hidden = true;
          }
        } catch (glazingErr) {
          console.error('UV Glazing add error:', glazingErr);
          glazingCheckbox.checked = false;
        }
      }

      // Hand the ATC trigger to cart-drawer so it can restore focus on close
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && submitButton && typeof cartDrawer.setActiveElement === 'function') {
        cartDrawer.setActiveElement(submitButton);
      }

      // Dispatch events AFTER both product and glazing are in the cart
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item, trigger: submitButton }
      }));

      // Also dispatch cart:updated for any other listeners
      document.dispatchEvent(new CustomEvent('cart:updated'));

      /* TPL patch 2026-04-25 — cart count must update before the drawer
         opens. Previously this relied on cart-drawer.refresh() to fetch
         /cart.js and dispatch cart:count-updated, which left the header
         counter showing [0] for several seconds after a successful add and
         made customers think the click failed. Fetch the count here and
         dispatch immediately so the header reflects state on the next
         frame, regardless of whether cart-drawer is on the page. */
      fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart || typeof cart.item_count !== 'number') return;
          document.dispatchEvent(new CustomEvent('cart:count-updated', {
            detail: { count: cart.item_count }
          }));
        })
        .catch(function () { /* swallow — drawer.refresh() is the fallback */ });

      /* Accessibility announce hook — Session E adds the <span id="tpl-live-announcer">
         element to layout/theme.liquid. No-ops gracefully until then. */
      const announcer = document.getElementById('tpl-live-announcer');
      if (announcer) {
        /* TPL patch 2026-04-17 (grill v2 #11): title is from server JSON but
           run through safeStr defensively. replaceChildren + text node is
           already safe (no HTML parse), keep for robustness. */
        const announceText = `Added ${safeStr(item.product_title || item.title || 'item')} to cart.`;
        announcer.replaceChildren(document.createTextNode(announceText));
      }

      // Brief success state
      if (submitButton) {
        hideSpinner(submitButton);
        const btnText = submitButton.querySelector('.btn-text');
        if (btnText) {
          btnText.textContent = 'Added \u2713';
        } else {
          submitButton.textContent = 'Added \u2713';
        }

        setTimeout(() => {
          submitButton.disabled = false;
          submitButton.classList.remove('is-loading');
          const btnText = submitButton.querySelector('.btn-text');
          if (btnText) {
            btnText.textContent = defaultAddText;
          } else {
            submitButton.textContent = originalText;
          }
        }, 1500);
      }

    } catch (error) {
      console.error('Add to cart error:', error);

      // D-3: surface error via role="alert" wrapper for screen readers.
      // Button text fallback retained for sighted users who don't see the wrapper.
      const errMsg = (error && error.message) || 'Error - Try Again';
      showFormError(form, errMsg);

      if (submitButton) {
        hideSpinner(submitButton);
        submitButton.disabled = false;
        submitButton.classList.remove('is-loading');
        const btnText = submitButton.querySelector('.btn-text');
        if (btnText) {
          btnText.textContent = errMsg;
        } else {
          submitButton.textContent = errMsg;
        }

        setTimeout(() => {
          const btnText = submitButton.querySelector('.btn-text');
          if (btnText) {
            btnText.textContent = defaultAddText;
          } else {
            submitButton.textContent = originalText;
          }
        }, 3000);
      }
    } finally {
      isSubmitting = false;
    }
  }, true);
})();
